// Core
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { Route, Switch, Redirect, useRouteMatch, useHistory, NavLink, Link, useLocation } from 'react-router-dom';
import {
  Button,
  Box,
  Flex,
  Grid,
  Text,
  Image,
  useColorMode,
  useColorModeValue,
} from '@chakra-ui/react';

// Accets
import { apiService } from 'services/ApiService';
import { formatDateWithSuffix } from 'services/Utils';
import profileSub from 'assets/img/profile-sub.png';
import emptyPlan from 'assets/img/empty-plan.png';
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import { ReactComponent as LinesWhite } from 'assets/svg/lines-white.svg';

// Components
import Card from 'components/Card/Card';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';

const Subscriptions = ({ userData }) => {
	const bg = useColorModeValue('white', '#0A1027');
	const border = useColorModeValue('1px solid #CBE7FF', '1px solid #262D47');
	const boxShadow = useColorModeValue('unset', '0px 1px 3px 0px rgba(0, 0, 0, 0.10),0px 1px 2px 0px rgba(0, 0, 0, 0.06)');
	const styleStatus = {
		height: '22px',
  	borderRadius: '100px',
		padding: '2px 12px 2px 10px',
  	bg: 'linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)', 
  	fontSize: '15px',
  	color: 'white',
  	fontWeight: '700',
  	fontStyle: 'italic',
		letterSpacing: '-1px',
		textTransform: 'uppercase'
	};

	const { colorMode } = useColorMode();
	const isDark = colorMode === 'dark';
	
	const history = useHistory();

	const { data: activePlans, isLoading } = useQuery({
    queryKey: ['active-plans'],
    queryFn: async () => {
        const response = await apiService.getSubscriptions({status: 'active'});
        return response.data;
    },
    refetchOnWindowFocus: false
  });

  const [isLoadingCustomerPortal, setIsLoadingCustomerPortal] = useState(false);
  const goToCustomerPortal = useCallback(async () => {
    setIsLoadingCustomerPortal(true);
    const response = await apiService.goToCustomerPortal();

    if (response.message === 'Success') {
      window.location.href = response.data;
    } 

    setIsLoadingCustomerPortal(false);
  }, []);

	return (
		<>
			<Breadcrumbs target='Membership' url='/account/membership' current='My Subscriptions' />

			<Grid
        templateColumns={{ sm: '1fr', lg: '1fr 1fr' }}
        templateRows="1fr"
        pb="8px"
        pt="8px"
        gap="16px">
        {activePlans && activePlans?.map((row, index) => (
        <Card 
        	key={index} 
        	padding="16px 12px"
        	bg={userData?.activePlan?.canceledAt === null && row?.periodEnd ? 'initial' : isDark ? '#262B40' : '#CCD7F3'} 
        	overflow="hidden">

        	{ isDark && 
	        	<Box 
	        		w="100%"
	        		h="100px"
	        		borderRadius="full"
							bg="#099BFF"
							filter="blur(220px)"
							position="absolute"
							top="-100%"
							left="0px"
	        	/>
	        }

        	{ isDark ? 
        		<Lines className="abstract-lines" style={{zIndex: -1, transform: 'scale(1.8)', opacity: 0.3}} /> 
        		: 
        		<LinesWhite className="abstract-lines" style={{zIndex: -1, opacity: 0.3}} />
        	}

        	<Image 
					zIndex="3"
					opacity="0.2"
					src={row?.plan?.image || emptyPlan} alt="Profile" 
					right="-15%" top="-20%" objectFit="cover" position="absolute" w="300px" h="300px"/>

					<Flex w="100%" justifyContent="space-between" zIndex="9">
	        	<Flex gap="12px" zIndex="1" position="relative" zIndex="3">
	          	<Box
							  w="64px"
							  h="64px"
							  boxShadow="0px 2.56px 9.344px 0px rgba(0, 0, 0, 0.25)"
							  borderRadius="12px">
							  <Image src={row?.plan?.image || emptyPlan} alt="Plan" />
							</Box>

							<Text variant="title-lg" w={{base: '200px'}} mt="-2px" fontSize="20px" fontStyle="normal" fontWeight="800">
							    {row?.plan?.name}
							</Text>
						</Flex>

						{ row?.canceledAt === null ? (
						  <Text style={styleStatus} bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)">Active</Text>
						) : row?.periodEnd ? (		
					    <Text style={styleStatus} bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)">Active</Text>
						) : (
							<Text style={styleStatus} bg="#6B7795">Expired</Text>
						)}
					</Flex>

					<Box zIndex="3" position="relative" mt="8px">
				    { userData?.activePlan &&
				    	<Text variant="title-lg" fontSize="24px" fontStyle="normal" fontWeight="700">
						    ${row?.plan?.price / 100} <span style={{fontSize: '17px', marginLeft: '-5px'}}>/{row?.plan?.paymentType === 'monthly' ? 'month' : 'year' }</span>
							</Text>
				    }

				    <Box mt="8px">
							<Text variant="title-lg" fontSize="15px">{ row?.canceledAt === null ? 'Next payment on' : 'Subscription Canceled'} </Text>
							
							{ row?.canceledAt === null ? (
							    <Text variant="title-lg" fontSize="15px">{formatDateWithSuffix(row?.periodEnd)} for {row?.price / 100}$</Text>
							) : row?.periodEnd ? (		
						    	<Text variant="title-lg" fontSize="15px">No upcoming payments. Your plan features remain available until {formatDateWithSuffix(row?.periodEnd)}</Text>
							) : (<></>)}
						</Box>
					</Box>

					{ userData?.activePlan?.canceledAt === null ? 
						<Button 
							onClick={goToCustomerPortal}
            	isLoading={isLoadingCustomerPortal}
							zIndex="9" variant="solid-md" mt="12px" w={{base: '100%', sm: '222px', lg: '100%'}}>
							Manage Subscription
						</Button>
						: row?.periodEnd ? 
						<Button
							onClick={goToCustomerPortal}
              isLoading={isLoadingCustomerPortal} 
							zIndex="9" variant="solid-md" mt="12px" w={{base: '100%', sm: '222px', lg: '100%'}}>
							Renew subscription
						</Button>
						: <></>
					}

	      </Card>
	      ))}
	    </Grid>
		</>
	);
};

export default Subscriptions;